import {
  ANNOTATION_LOCATION,
  ANNOTATION_ORIGIN_LOCATION,
  Entity,
  getCompoundEntityRef,
  parseLocationRef,
} from '@backstage/catalog-model';
import type { CatalogApi, Location } from '@backstage/catalog-client';

export type CatalogEntityUnregisterCatalogApi = Pick<
  CatalogApi,
  | 'getLocationByRef'
  | 'getLocationByEntity'
  | 'getLocations'
  | 'removeEntityByUid'
  | 'removeLocationById'
>;

export type CatalogEntityUnregisterResult =
  | { type: 'location'; location: Location }
  | { type: 'entity' };

const normalizeCatalogFilePath = (path: string) => {
  let decodedPath = path;
  try {
    decodedPath = decodeURI(path);
  } catch {
    // Keep the original path if it is not a valid URI sequence.
  }

  return decodedPath
    .replace(/\/-\/blob\//, '/-/file/')
    .replace(/\/-\/raw\//, '/-/file/');
};

const normalizedLocationTarget = (target: string) => {
  const trimmed = target.trim();

  try {
    const url = new URL(trimmed);
    url.hash = '';
    url.search = '';
    url.pathname = normalizeCatalogFilePath(url.pathname);
    return url.toString().replace(/\/$/, '');
  } catch {
    return normalizeCatalogFilePath(trimmed)
      .replace(/[?#].*$/, '')
      .replace(/\/$/, '');
  }
};

const uniqueLocationRefs = (entity: Entity) => {
  const annotations = entity.metadata.annotations ?? {};
  const refs = [
    annotations[ANNOTATION_ORIGIN_LOCATION],
    annotations[ANNOTATION_LOCATION],
  ].filter((ref): ref is string => Boolean(ref));

  return Array.from(new Set(refs));
};

const parseLocationRefSafely = (locationRef: string) => {
  try {
    return parseLocationRef(locationRef);
  } catch {
    return undefined;
  }
};

export const getCatalogEntityLocationRefs = uniqueLocationRefs;

export const resolveCatalogEntityLocation = async (
  entity: Entity,
  catalogApi: CatalogEntityUnregisterCatalogApi,
): Promise<Location | undefined> => {
  const locationRefs = uniqueLocationRefs(entity);

  for (const locationRef of locationRefs) {
    try {
      const location = await catalogApi.getLocationByRef(locationRef);
      if (location) {
        return location;
      }
    } catch {
      // Some provider/bootstrap refs are not registered catalog Locations.
    }
  }

  try {
    const location = await catalogApi.getLocationByEntity(
      getCompoundEntityRef(entity),
    );
    if (location) {
      return location;
    }
  } catch {
    // Provider-owned entities and non-location sources may not have a
    // registered catalog Location associated with the entity ref.
  }

  const parsedRefs = locationRefs
    .map(parseLocationRefSafely)
    .filter((ref): ref is { type: string; target: string } => Boolean(ref));

  if (parsedRefs.length === 0) {
    return undefined;
  }

  let locations: Location[];
  try {
    locations = (await catalogApi.getLocations()).items;
  } catch {
    return undefined;
  }

  for (const parsedRef of parsedRefs) {
    const matchingLocation = locations.find(
      location =>
        location.type === parsedRef.type &&
        normalizedLocationTarget(location.target) ===
          normalizedLocationTarget(parsedRef.target),
    );

    if (matchingLocation) {
      return matchingLocation;
    }
  }

  return undefined;
};

export const unregisterCatalogEntitySource = async (
  entity: Entity,
  catalogApi: CatalogEntityUnregisterCatalogApi,
): Promise<CatalogEntityUnregisterResult> => {
  const location = await resolveCatalogEntityLocation(entity, catalogApi);

  if (location) {
    await catalogApi.removeLocationById(location.id);
    return { type: 'location', location };
  }

  const uid = entity.metadata.uid;
  if (!uid) {
    throw new Error(
      `Cannot delete ${entity.kind}:${entity.metadata.namespace ?? 'default'}/${
        entity.metadata.name
      } because the entity has no metadata.uid`,
    );
  }

  await catalogApi.removeEntityByUid(uid);
  return { type: 'entity' };
};
